import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { AppCard } from './AppCard';
import { EmptyState } from './EmptyState';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import type { WeeklyAttendanceEmployeeReport } from '../types/weeklyAttendanceReport';
import {
  formatWeekRangeLabel,
  formatWeeklyDateLabel,
  formatWeeklyHours,
  formatWeeklyPermissionStatus,
  formatWeeklyStatusLabel,
  weeklyCheckInDisplay,
  weeklyCheckOutDisplay,
  weeklyHighlightCheckOut,
} from '../utils/weeklyAttendanceReport';
import { resolveWorkHoursCoverage } from '../utils/workHoursCoverage';
import {
  actualTotalWorkMinutes,
  formatTotalWorkHours,
  resolveAttendanceSpreadMinutes,
  resolveTotalHoursCoverage,
} from '../utils/totalHours';

type Props = {
  report: WeeklyAttendanceEmployeeReport | null;
  errorMessage?: string;
  onRetry: () => void;
  onViewDetails: () => void;
};

function statusColor(
  tone: WeeklyAttendanceEmployeeReport['days'][number]['statusTone'] | undefined,
  statusCode: string,
  palette: ColorPalette,
): string {
  switch (tone) {
    case 'present':
      return palette.success;
    case 'absent':
      return palette.error;
    case 'late':
      return palette.error;
    case 'leave':
      return palette.warning;
    case 'wfh':
      return palette.info;
    case 'shortHours':
      return palette.warning;
    default:
      break;
  }
  switch (statusCode) {
    case 'ON_TIME':
    case 'PRESENT':
      return palette.success;
    case 'ABSENT':
    case 'LOP':
    case 'HALF_DAY':
      return palette.error;
    case 'LATE':
    case 'SHORT_HOURS':
    case 'MISSING_CHECKOUT':
      return palette.warning;
    default:
      return palette.textSecondary;
  }
}

function dayTotalWorkMinutes(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): number | null {
  return actualTotalWorkMinutes({
    workedMinutes: day.workedMinutes,
    breakMinutes: day.lunchBreakMinutes,
    grossWorkMinutes: day.totalMinutes,
  });
}

function daySpreadMinutes(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): number | null {
  return resolveAttendanceSpreadMinutes({
    spreadMinutes: day.spreadMinutes,
    expectedWorkMinutes: day.expectedWorkMinutes,
    policyLunchBreakMinutes: day.policyLunchBreakMinutes,
    actualBreakMinutes: day.lunchBreakMinutes,
  });
}

function weeklyTotalHoursLabel(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): string {
  if (day.totalHours) return day.totalHours;
  return formatTotalWorkHours(dayTotalWorkMinutes(day));
}

/** Total Work Hours vs Spread Duration: green when covered, red when short. */
function totalHoursColor(
  day: WeeklyAttendanceEmployeeReport['days'][number],
  palette: ColorPalette,
): string {
  const label = weeklyTotalHoursLabel(day);
  if (label === '—' || label === '') return palette.textSecondary;
  const coverage = resolveTotalHoursCoverage(
    dayTotalWorkMinutes(day),
    daySpreadMinutes(day),
  );
  if (coverage === 'not_covered') return palette.error;
  if (coverage === 'covered') return palette.success;
  return palette.textSecondary;
}

/** Worked Hours vs Expected Work Hours: green when met, red when short. */
function workedHoursColor(
  day: WeeklyAttendanceEmployeeReport['days'][number],
  palette: ColorPalette,
): string {
  const label = day.workHours ?? formatWeeklyHours(day.workedMinutes);
  if (label === '—' || label === '') return palette.textSecondary;
  const coverage = resolveWorkHoursCoverage(
    day.workedMinutes,
    day.expectedWorkMinutes,
  );
  if (coverage === 'not_covered') return palette.error;
  if (coverage === 'covered') return palette.success;
  return palette.textSecondary;
}

export function DashboardWeeklyAttendance({
  report,
  errorMessage,
  onRetry,
  onViewDetails,
}: Props) {
  const palette = useAppColors();
  const { isCompact, isWide } = useResponsiveLayout();
  const dayWidth = isCompact ? '100%' : isWide ? '31%' : '48%';

  const themed = useMemo(
    () => ({
      sectionTitle: { ...typography.sectionTitle, color: palette.textPrimary },
      week: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      viewDetails: {
        ...typography.caption,
        color: palette.primary,
        fontWeight: '700' as const,
      },
      dayDate: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      fieldLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      fieldValue: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '600' as const,
      },
      errorText: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  return (
    <View style={styles.section}>
      <View style={styles.headerRow}>
        <Text style={themed.sectionTitle}>Weekly Attendance</Text>
        {report ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="View weekly attendance details"
            onPress={onViewDetails}
          >
            <Text style={themed.viewDetails}>View details</Text>
          </Pressable>
        ) : null}
      </View>
      {report ? (
        <Text style={themed.week}>
          {formatWeekRangeLabel(report.weekStart, report.weekEnd)}
        </Text>
      ) : null}

      {errorMessage ? (
        <View style={styles.errorBlock} accessibilityRole="alert">
          <Text style={themed.errorText}>{errorMessage}</Text>
          <AppButton title="Retry" variant="secondary" onPress={onRetry} />
        </View>
      ) : !report ? (
        <EmptyState
          title="No data available"
          message="No attendance was found for this week."
        />
      ) : (
          <View style={styles.wrapRow}>
            {report.days.map(day => {
              const permission = formatWeeklyPermissionStatus(day);
              const status = formatWeeklyStatusLabel(day);
              return (
                <AppCard
                  key={day.date}
                  style={[styles.dayCard, { width: dayWidth }]}
                >
                  <View style={styles.dayHeader}>
                    <Text style={themed.dayDate}>
                      {formatWeeklyDateLabel(day.date)}
                    </Text>
                    <Text
                      style={[
                        styles.status,
                        {
                          color: statusColor(
                            day.statusTone,
                            day.statusCode,
                            palette,
                          ),
                        },
                      ]}
                    >
                      {status}
                    </Text>
                  </View>
                  <View style={styles.dayField}>
                    <Text style={themed.fieldLabel}>Check In</Text>
                    <Text style={themed.fieldValue}>
                      {weeklyCheckInDisplay(day)}
                    </Text>
                  </View>
                  <View style={styles.dayField}>
                    <Text style={themed.fieldLabel}>Check Out</Text>
                    <Text
                      style={[
                        themed.fieldValue,
                        weeklyHighlightCheckOut(day)
                          ? { color: palette.error, fontWeight: '700' }
                          : null,
                      ]}
                    >
                      {weeklyCheckOutDisplay(day)}
                    </Text>
                  </View>
                  <View style={styles.dayMeta}>
                    <View style={styles.dayFieldGrow}>
                      <Text style={themed.fieldLabel}>Break</Text>
                      <Text style={themed.fieldValue}>
                        {day.breakTime ?? formatWeeklyHours(day.lunchBreakMinutes)}
                      </Text>
                    </View>
                    <View style={styles.dayFieldGrow}>
                      <Text style={themed.fieldLabel}>Work Hours</Text>
                      <Text
                        style={[
                          themed.fieldValue,
                          { color: workedHoursColor(day, palette) },
                        ]}
                      >
                        {day.workHours ?? formatWeeklyHours(day.workedMinutes)}
                      </Text>
                    </View>
                    <View style={styles.dayFieldGrow}>
                      <Text style={themed.fieldLabel}>Total Work Hours</Text>
                      <Text
                        style={[
                          themed.fieldValue,
                          { color: totalHoursColor(day, palette) },
                        ]}
                      >
                        {weeklyTotalHoursLabel(day)}
                      </Text>
                    </View>
                  </View>
                  {permission !== '—' ? (
                    <Text style={[themed.fieldValue, { color: palette.textSecondary }]}>
                      {permission}
                    </Text>
                  ) : null}
                </AppCard>
              );
            })}
          </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: { gap: spacing.sm, minWidth: 0, width: '100%' },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minWidth: 0,
  },
  wrapRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minWidth: 0,
  },
  dayCard: {
    gap: spacing.sm,
    minWidth: 0,
    maxWidth: '100%',
    flexGrow: 0,
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  status: { ...typography.caption, fontWeight: '700', flexShrink: 1, textAlign: 'right' },
  dayField: { gap: 2, minWidth: 0 },
  dayMeta: { flexDirection: 'row', gap: spacing.md, minWidth: 0 },
  dayFieldGrow: { flex: 1, gap: 2, minWidth: 0 },
  errorBlock: { gap: spacing.sm },
});
